/**
 * The exercise rail of a math homework: an outline labelled positionally ("Exercise 3"), with the
 * type and a snippet of the prompt's first text block. Every exercise is minted with an id, so
 * unlike the chapter rail this one is addressed by id.
 */
import { computed, ref } from 'vue'

import type { ComputedRef, Ref } from 'vue'
import type { NativeSelectOption } from '@shared/ui/fields'
import type { MathExercise } from './store/interfaces'

import { MathExerciseType } from './enums'
import { useAssignmentEditorStore } from './store'
import { getMathExerciseTypeLabel } from './assignment-template-options'

const PROMPT_SUMMARY_LENGTH = 40

interface MathExerciseListItem {
  id: string
  /** 1-based. */
  position: number
  /** `Exercise 3`. */
  label: string
  typeLabel: string
  /** Opening characters of the prompt's first text block. */
  summary: string
  isSelected: boolean
  isFirstInOrder: boolean
  isLastInOrder: boolean
}

interface UseMathExerciseListReturn {
  refExercises: ComputedRef<MathExerciseListItem[]>
  refHasExercises: ComputedRef<boolean>
  /** The type the next "Add exercise" creates; it sticks, so a run of one type is one click each. */
  refNewType: Ref<MathExerciseType>
  refNewTypeOptions: ComputedRef<NativeSelectOption[]>
  setNewType: (value: string) => void
  selectExercise: (exerciseId: string) => void
  addExercise: () => void
  removeExercise: (exerciseId: string) => void
  moveExerciseUp: (exerciseId: string) => void
  moveExerciseDown: (exerciseId: string) => void
}

const isKnownType = (value: unknown): value is MathExerciseType =>
  Object.values(MathExerciseType).includes(value as MathExerciseType)

/** Stored `data` is unvalidated: a prompt that is not a list, or a text block with no string. */
const summariseExercise = (exercise: MathExercise): string => {
  const prompt: unknown = exercise?.prompt
  const blocks = Array.isArray(prompt) ? prompt as MathExercise['prompt'] : []
  const text = blocks.find(block => block?.type === 'text')
  const raw: unknown = text?.type === 'text' ? text.value : ''
  const value = typeof raw === 'string' ? raw.trim() : ''

  if (!value) {
    return blocks.some(block => block?.type === 'image') ? 'Image prompt' : 'No prompt yet'
  }

  return value.length > PROMPT_SUMMARY_LENGTH
    ? `${value.slice(0, PROMPT_SUMMARY_LENGTH)}…`
    : value
}

export const useMathExerciseList = (): UseMathExerciseListReturn => {
  const store = useAssignmentEditorStore()

  const refExercises = computed<MathExerciseListItem[]>(() => {
    const exercises = store.getExercises

    return exercises.map((exercise, index) => ({
      id: exercise?.id ?? '',
      position: index + 1,
      label: `Exercise ${index + 1}`,
      typeLabel: isKnownType(exercise?.type) ? getMathExerciseTypeLabel(exercise.type) : 'Unknown type',
      summary: summariseExercise(exercise),
      isSelected: !!exercise?.id && exercise.id === store.getSelectedExerciseId,
      isFirstInOrder: index === 0,
      isLastInOrder: index === exercises.length - 1,
    }))
  })

  const refHasExercises = computed<boolean>(() => store.hasExercises)

  const refNewType = ref<MathExerciseType>(MathExerciseType.MultipleChoice)

  const refNewTypeOptions = computed<NativeSelectOption[]>(() =>
    Object.values(MathExerciseType).map(value => ({
      id: value,
      value,
      text: getMathExerciseTypeLabel(value),
      selected: value === refNewType.value,
    })))

  const setNewType = (value: string): void => {
    if (isKnownType(value)) {
      refNewType.value = value
    }
  }

  return {
    refExercises,
    refHasExercises,
    refNewType,
    refNewTypeOptions,
    setNewType,
    selectExercise: (exerciseId: string): void => { store.selectExercise(exerciseId) },
    addExercise: (): void => { store.addExercise(refNewType.value) },
    removeExercise: (exerciseId: string): void => { store.removeExercise(exerciseId) },
    moveExerciseUp: (exerciseId: string): void => { store.moveExerciseUp(exerciseId) },
    moveExerciseDown: (exerciseId: string): void => { store.moveExerciseDown(exerciseId) },
  }
}
